import React from 'react';
import {
  Component,
  FilterCombo,
  HBox,
  noDataDropDownRender,
  Position,
  PuzzleRadio,
  VBox
} from "puzzle";
import AssignDialogDropdown from "./AssignDialogDropdown/AssignDialogDropdown";
import { Field } from "../../../../UI/Field/Field";
import { USER } from "./AssignDialogDropdown/Types";
import { SUPPLIER } from "../Types";
import { resetFocus } from "../../../../util/Util";
import _ from 'lodash';

type STATE = {
  organizationUserSelected: boolean
  organizationUserValue: USER
  supplierUserValue: USER | null
  supplierValue: USER
};

type PROPS = {
  event: any
  includeRacu: boolean // (https://techqa1.translations.com/browse/PDII-29347)
  onChange: Function
  phase: string
  phaseSuppliers: SUPPLIER[]
  projectId: number
};

class AssignSupplier extends Component <PROPS, STATE> {
  private blockAction: boolean = false; //block autoPopulate if it's not triggered by supplierRadio
  private supplierRef: any;

  constructor(props: PROPS) {
    super(props);

    this.state = {
      organizationUserSelected: undefined,
      organizationUserValue: null,
      supplierUserValue: null,
      supplierValue: null // It is the value of a supplier. Value of the bottom left combobox.
    };

    this.supplierRef = React.createRef();
  }

  /**
   * Returns array of all selected suppliers for currnet phase
   */
  getSelectedPhaseSuppliersGroups = (): SUPPLIER[] | [] => {
    const selectedPhaseSuppliers = this.props.phaseSuppliers.filter((supplier: SUPPLIER) => supplier.selected);
    return selectedPhaseSuppliers;
  };

  /**
   * Handles changes of radio buttons by updating states and calling handleSaveButton()
   * @param user
   * @param supplierRadioClick
   */
  handleRadioChange = (user: string, supplierRadioClick: boolean = false) => {
    let updateState: any = {
      organizationUserValue: null,
      organizationUserSelected: true,
      supplierValue: null,
      supplierUserValue: null
    };
    if (user !== 'organizationUser') {
      updateState = {
        organizationUserSelected: false,
        organizationUserValue: null
      };

      if (supplierRadioClick) {
        this.blockAction = false;
        const groups = this.getSelectedPhaseSuppliersGroups();
        if (groups.length === 1 && this.state.supplierValue !== groups[0]) {
          updateState = {
            ...updateState,
            supplierValue: groups[0],
            supplierUserValue: null
          };
        }
      }
    }

    this.setState({ ...updateState }, this.handleSaveButton);
  }

  /**
   * Handles dropdown value changes and is adding them to state (It is being passed to child components)
   * @param user
   * @param value
   */
  onDropdownValueChange = (user: string, value: USER) => {
    this.blockAction = true;
    switch (user) {
      case 'organizationUser':
        this.setState({ organizationUserValue: value }, this.handleSaveButton);
        break;
      case 'supplier':
        if (this.state.supplierValue !== value) {
          this.setState({ supplierValue: value, supplierUserValue: null }, () => {
            resetFocus(this.supplierRef);
          }); // if state of supplier is Falsy(null) assign supplierUserValue State to null, else keep it as it was before
        }
        break;
      case 'supplierUser':
        this.setState({ supplierUserValue: value }, this.handleSaveButton);
        break;
    }
  }

  /**
   * Handles if save button should be enabled or disabled
   */
  handleSaveButton = () => {
    const { organizationUserSelected, organizationUserValue, supplierUserValue } = this.state
    let saveButtonDisabled = !organizationUserValue;
    if (!organizationUserSelected) {
      saveButtonDisabled = !supplierUserValue;
    }

    this.dispatch('assignDialogSaveButtonUpdate', Position.end, { saveButtonDisabled });
  }

  /**
   * Passed to child components. Used to update radio and save buttons
   * @param user
   */
  onDropdownFocus = (user: string) => {
    this.handleRadioChange(user);
  }

  /**
   * Save selected value handler
   */
  updateSuppliersList = () => {
    let {
      organizationUserValue,
      supplierUserValue,
      supplierValue
    } = this.state;

    let lastModified: any = {
      claimed: false,
      removePossible: true,
      selected: true,
      UIassigned: true
    };

    let editedEventData;

    if (organizationUserValue) {
      const {
        firstName,
        lastName,
        userId,
        userName
      } = organizationUserValue;

      editedEventData = {
        lastModified: [{
          ...lastModified,
          supplierPhase: this.props.phase,
          editable: true,
          orgUser: true,
          supplierId: userId,
          supplierName: firstName + ' ' + lastName,
          userName: userName
        }]
      };

    } else if (supplierUserValue) {
      const {
        firstName,
        lastName,
        userId,
        userName
      } = supplierUserValue;

      if (supplierUserValue.selected) {
        lastModified = [{
          ...lastModified,
          ...supplierValue,
          userName: userName,
          supplierUser: true,
          firstName,
          lastName,
          userId
        }];

        if (lastModified[0]?.combinePhaseUser) {
          delete lastModified[0]?.editable;
        }

        editedEventData = {
          lastModified
        };

      } else {
        editedEventData = {
          lastModified: [{ ...supplierValue }],
          supplierUserData: {
            ...lastModified,
            userName: userName,
            supplierUser: true,
            firstName,
            lastName,
            userId
          }
        };
      }
    }

    this.props.onChange({
      ...this.props.event,
      ...editedEventData,
    })
  };

  renderBody() {
    const {
      organizationUserSelected,
      organizationUserValue,
      supplierValue,
      supplierUserValue
    } = this.state
    const { phaseSuppliers } = this.props

    const noDataRender = phaseSuppliers.length ? 'searching' : 'noSupplierAvailable';
    const groups = this.getSelectedPhaseSuppliersGroups();

    return (
      <VBox gap={5}>
        <HBox gap={3}>
          <PuzzleRadio
            checked={organizationUserSelected === undefined ? false : organizationUserSelected}
            id={'organizationUserRadioButton'}
            onChange={this.handleRadioChange.bind(this, 'organizationUser', false)}
          />
          <AssignDialogDropdown
            {...this.props}
            name={'organizationUser'}
            moduleId={'organizationUser'}
            disabled={false}
            supplierValue={undefined}
            onDropdownFocus={this.onDropdownFocus}
            value={organizationUserValue}
            onDropdownChangeParent={this.onDropdownValueChange}
          />
        </HBox>
        <HBox gap={3}>
          <PuzzleRadio
            checked={organizationUserSelected === undefined ? false : !organizationUserSelected}
            id={'supplierUserRadioButton'}
            onChange={this.handleRadioChange.bind(this, 'supplier', true)}
          />

          <Field>
            <FilterCombo
              disabled={false}
              onFocus={this.onDropdownFocus.bind(this, 'supplier')}
              label={this.translate('supplier')}
              data={phaseSuppliers}
              listNoDataRender={() => noDataDropDownRender(this.translate(noDataRender))}
              name='supplier'
              onChange={(event: any) => this.onDropdownValueChange('supplier', event.value)}
              popupSettings={{ className: 'pd-assign-dropdown' }}
              textField="supplierName"
              translateMethod={this.translate.bind(this)}
              value={this.state.supplierValue}
              ref={this.supplierRef}
            />
          </Field>

          <AssignDialogDropdown
            {...this.props}
            autoPopulate={groups.length === 1 && (_.isEqual(supplierValue, groups[0]) && !this.blockAction)}
            disabled={supplierValue === null || supplierValue === undefined}
            name={'supplierUser'}
            moduleId={'supplierUser'}
            value={!supplierValue ? null : supplierUserValue}
            supplierValue={this.state.supplierValue}
            onDropdownFocus={this.onDropdownFocus}
            onDropdownChangeParent={this.onDropdownValueChange}
          />
        </HBox>
      </VBox>
    );
  }


}

export default AssignSupplier
